'use client';

import { usePathname } from 'next/navigation';
import { useEffect } from 'react';
import { useAuthContext } from '@/lib/auth/AuthProvider';

declare global {
  interface Window {
    Tawk_API?: {
      showWidget?: () => void;
      hideWidget?: () => void;
      maximize?: () => void;
      minimize?: () => void;
      onLoad?: () => void;
      onChatMinimized?: () => void;
      setAttributes?: (
        attributes: Record<string, string>,
        callback?: (error?: unknown) => void,
      ) => void;
    };
    Tawk_LoadStart?: Date;
  }
}

// BottomNavBar is rendered below 1024px (see BottomNavBar `min-[1024px]:hidden`).
// Hide the Tawk widget on that range so it doesn't overlap the bottom nav,
// and open the chat fullscreen on those viewports — the floating panel is
// awkward when the bottom nav already occupies the bottom of the screen.
const HIDE_BREAKPOINT = 1023;

function isCompactViewport() {
  return typeof window !== 'undefined' && window.innerWidth <= HIDE_BREAKPOINT;
}

// Tracks whether the chat is currently rendered in fullscreen mode so the
// MutationObserver below doesn't fight the fullscreen styles.
let fullscreenActive = false;

/** Shift every Tawk-owned fixed container up so it clears the bottom nav.
 *  Uses setProperty with !important to beat Tawk's own inline styles,
 *  and a MutationObserver to catch late-rendered elements. */
function shiftTawkElements() {
  if (fullscreenActive) return;
  // Only target containers that actually wrap a Tawk iframe — matching by
  // z-index alone catches unrelated portals (e.g. Radix dialogs) and shifts
  // their overlays up, leaving the bottom 90px of the viewport undimmed.
  document.querySelectorAll<HTMLIFrameElement>('iframe[src*="tawk.to"]').forEach((iframe) => {
    let el: HTMLElement | null = iframe.parentElement;
    while (el && el.parentElement !== document.body) {
      el = el.parentElement;
    }
    if (el && el.parentElement === document.body) {
      el.style.setProperty('bottom', '90px', 'important');
    }
  });
}

let tawkObserver: MutationObserver | null = null;

function adjustWidgetPosition() {
  shiftTawkElements();
  if (tawkObserver) return;
  tawkObserver = new MutationObserver(() => shiftTawkElements());
  tawkObserver.observe(document.body, { childList: true, subtree: true, attributes: true });
}

const FULLSCREEN_CSS = `
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  height: 100dvh !important;
  max-width: 100vw !important;
  max-height: 100vh !important;
  max-height: 100dvh !important;
  min-width: 100vw !important;
  min-height: 100vh !important;
  min-height: 100dvh !important;
  border-radius: 0 !important;
  margin: 0 !important;
  z-index: 2147483646 !important;
`;

function applyFullscreenToTawk() {
  fullscreenActive = true;
  // Find all iframes from tawk.to and their parent containers
  const iframes = document.querySelectorAll<HTMLIFrameElement>('iframe[src*="tawk.to"]');
  iframes.forEach((iframe) => {
    iframe.style.cssText = FULLSCREEN_CSS;
    // Also force the parent containers to be fullscreen
    let parent = iframe.parentElement;
    while (parent && parent !== document.body) {
      if (parent.style.position === 'fixed' || getComputedStyle(parent).position === 'fixed') {
        parent.style.cssText += FULLSCREEN_CSS;
      }
      parent = parent.parentElement;
    }
  });
}

function resetTawkStyles() {
  fullscreenActive = false;
  const iframes = document.querySelectorAll<HTMLIFrameElement>('iframe[src*="tawk.to"]');
  iframes.forEach((iframe) => {
    iframe.style.cssText = '';
    let parent = iframe.parentElement;
    while (parent && parent !== document.body) {
      if (parent.style.zIndex === '2147483646') {
        parent.style.cssText = '';
      }
      parent = parent.parentElement;
    }
  });
}

// Tawk's embed script logs a bare `console.error(true)` in dev that pollutes
// the console, so it only loads in production builds. Set
// NEXT_PUBLIC_TAWK_ENABLED=true to force-enable locally when testing chat.
const TAWK_ENABLED =
  process.env.NODE_ENV === 'production' || process.env.NEXT_PUBLIC_TAWK_ENABLED === 'true';

function isTawkHiddenPath(pathname: string) {
  return pathname.replace(/^\/[a-z]{2}(?=\/|$)/, '').startsWith('/news');
}

/** The desktop bubble's box: px from the viewport's right and bottom edges, and its width. */
export const TAWK_BUBBLE = { right: 20, bottom: 20, width: 64 };

/** Whether the floating chat bubble occupies the bottom-right corner on desktop. */
export function showsTawkBubble(pathname: string) {
  return TAWK_ENABLED && !isTawkHiddenPath(pathname);
}

const TAWK_EMBED_SRC = 'https://embed.tawk.to/699ff25fd7a51a1c3eaaf62a/1jicclhd1';

// The widget pulls in a dozen script files and runs ~50 ms of main-thread
// work; nobody needs it in the first seconds of a page view. The script is
// injected once the browser is idle after load (or on the first interaction,
// whichever comes first), and immediately if the visitor asks for the chat.
const IDLE_INJECT_TIMEOUT_MS = 4000;
const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'scroll'] as const;

let scriptInjected = false;
let tawkLoaded = false;

/** Creates the `Tawk_API` hook object so callbacks can be attached before the script exists. */
function ensureTawkApi() {
  window.Tawk_API = window.Tawk_API || {};
  return window.Tawk_API;
}

function injectTawkScript() {
  if (scriptInjected || !TAWK_ENABLED) return;
  scriptInjected = true;

  const api = ensureTawkApi();
  window.Tawk_LoadStart = new Date();
  const prevOnLoad = api.onLoad;
  api.onLoad = () => {
    tawkLoaded = true;
    prevOnLoad?.();
  };

  const s1 = document.createElement('script');
  s1.async = true;
  s1.src = TAWK_EMBED_SRC;
  s1.charset = 'UTF-8';
  s1.setAttribute('crossorigin', '*');
  document.head.appendChild(s1);
}

/** Runs `fn` once the widget is ready; injects the script if that has not happened yet. */
function whenTawkReady(fn: () => void) {
  if (tawkLoaded) {
    fn();
    return;
  }
  const api = ensureTawkApi();
  const prevOnLoad = api.onLoad;
  api.onLoad = () => {
    prevOnLoad?.();
    fn();
  };
  injectTawkScript();
}

export function openTawkChat() {
  whenTawkReady(() => {
    // Below 1024px the widget is hidden by default — bring it back so the
    // chat panel can render, then maximize it.
    window.Tawk_API?.showWidget?.();
    window.Tawk_API?.maximize?.();

    if (!isCompactViewport()) return;
    document.body.style.overflow = 'hidden';
    // Apply fullscreen styles with retries (Tawk renders async)
    applyFullscreenToTawk();
    setTimeout(applyFullscreenToTawk, 100);
    setTimeout(applyFullscreenToTawk, 300);
    setTimeout(applyFullscreenToTawk, 600);
  });
}

export function TawkToChat() {
  const pathname = usePathname();
  const { user } = useAuthContext();

  const isNewsPage = isTawkHiddenPath(pathname);

  // Prepare the hook object synchronously so the effects below can attach
  // their callbacks, then defer the actual script until idle or interaction.
  useEffect(() => {
    if (!TAWK_ENABLED || scriptInjected) return;
    ensureTawkApi();

    const inject = () => {
      injectTawkScript();
      cleanup();
    };
    // Safari still lacks requestIdleCallback; fall back to a plain timer there.
    const hasIdleCallback = typeof window.requestIdleCallback === 'function';
    const idleHandle = hasIdleCallback
      ? window.requestIdleCallback(inject, { timeout: IDLE_INJECT_TIMEOUT_MS })
      : window.setTimeout(inject, IDLE_INJECT_TIMEOUT_MS);
    for (const type of INTERACTION_EVENTS) {
      window.addEventListener(type, inject, { once: true, passive: true });
    }
    function cleanup() {
      if (hasIdleCallback) window.cancelIdleCallback(idleHandle);
      else window.clearTimeout(idleHandle);
      for (const type of INTERACTION_EVENTS) window.removeEventListener(type, inject);
    }
    return cleanup;
  }, []);

  // When the user minimizes via Tawk's own UI, clean up. Re-hide the widget
  // on any viewport ≤HIDE_BREAKPOINT so it doesn't sit on top of BottomNavBar.
  useEffect(() => {
    if (!window.Tawk_API) return;
    window.Tawk_API.onChatMinimized = () => {
      const isHiddenViewport = window.matchMedia(`(max-width: ${HIDE_BREAKPOINT}px)`).matches;
      if (isCompactViewport()) resetTawkStyles();
      if (isHiddenViewport) {
        window.Tawk_API?.hideWidget?.();
        document.body.style.overflow = '';
      }
    };
  }, []);

  // Show/hide based on route and screen size — hide whenever BottomNavBar is
  // visible (≤1023px) so the widget never overlaps the bottom menu.
  useEffect(() => {
    const mediaQuery = window.matchMedia(`(max-width: ${HIDE_BREAKPOINT}px)`);

    const updateVisibility = () => {
      const api = window.Tawk_API;
      if (!api) return;

      if (isNewsPage || mediaQuery.matches) {
        api.hideWidget?.();
      } else {
        api.showWidget?.();
      }

      adjustWidgetPosition();
    };

    updateVisibility();

    // If Tawk hasn't loaded yet, queue the visibility via onLoad
    const api = window.Tawk_API;
    if (api) {
      const prevOnLoad = api.onLoad;
      api.onLoad = () => {
        prevOnLoad?.();
        updateVisibility();
        adjustWidgetPosition();
        // Retry after short delay since Tawk renders asynchronously
        setTimeout(adjustWidgetPosition, 500);
        // Set up minimize callback after load
        if (window.Tawk_API) {
          window.Tawk_API.onChatMinimized = () => {
            const isHiddenViewport = window.matchMedia(`(max-width: ${HIDE_BREAKPOINT}px)`).matches;
            if (isHiddenViewport) {
              window.Tawk_API?.hideWidget?.();
              document.body.style.overflow = '';
            }
          };
        }
      };
    }

    const handleResize = () => updateVisibility();
    mediaQuery.addEventListener('change', handleResize);
    return () => mediaQuery.removeEventListener('change', handleResize);
  }, [isNewsPage]);

  // Pass user email to Tawk.to
  useEffect(() => {
    const email = user?.email;
    if (!email) return;

    const setAttrs = () => {
      window.Tawk_API?.setAttributes?.(
        { email, ...(user.name ? { name: user.name } : {}) },
        () => {},
      );
    };

    if (window.Tawk_API?.setAttributes) {
      setAttrs();
    }

    const prevOnLoad = window.Tawk_API?.onLoad;
    if (window.Tawk_API) {
      window.Tawk_API.onLoad = () => {
        prevOnLoad?.();
        setAttrs();
      };
    }
  }, [user?.email, user?.name]);

  return null;
}
